import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Plus,
  Users, 
  FileText, 
  DollarSign,
  TrendingUp,
  RefreshCw,
  Filter as FilterIcon
} from 'lucide-react';
import { CadastrarParceiro, ParceirosList, ParceiroDetails, ParceiroFilters } from '@/components/Parceiros';
import { useParceiros } from '@/hooks/useParceiros';
import { ParceiroFilters as ParceiroFiltersType } from '@/types/parceiros';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsMobile } from '@/hooks/use-mobile';

export default function Parceiros() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParceiro, setSelectedParceiro] = useState<string | null>(null);
  const [cadastroOpen, setCadastroOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ParceiroFiltersType>({
    periodo: '30_dias',
    status: 'todos',
    ordenacao: 'valor_contratos'
  });
  
  const { parceiros, loading, error, refetch } = useParceiros(filters);
  const permissions = usePermissions();
  const isMobile = useIsMobile();
  const { userProfile } = permissions;
  const podeCriarParceiro = permissions.parceiros?.canCreate?.() && 
    (!permissions.isPartner() || (userProfile as { pode_criar_rede_parceiros?: boolean } | null)?.pode_criar_rede_parceiros);

  // Filtrar parceiros por busca (nome, email, cidade, estado)
  const filteredParceiros = parceiros?.filter(parceiro =>
    parceiro.parceiro_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parceiro.parceiro_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parceiro.parceiro_cidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parceiro.parceiro_estado?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Métricas agregadas: leads, propostas e contratos unificados (fotovoltaico + carregadores veiculares)
  const metricsAgregadas = {
    totalParceiros: parceiros?.length || 0,
    parceirosAtivos: parceiros?.filter(p => p.status).length || 0,
    totalLeads: parceiros?.reduce((sum, p) => sum + p.total_leads, 0) || 0,
    leadsEsteMes: parceiros?.reduce((sum, p) => sum + p.leads_30_dias, 0) || 0,
    totalPropostas:
      (parceiros?.reduce((sum, p) => sum + p.propostas_enviadas, 0) || 0) +
      (parceiros?.reduce((sum, p) => sum + (p.propostas_carregadores_enviadas ?? 0), 0) || 0),
    propostasEsteMes:
      (parceiros?.reduce((sum, p) => sum + p.propostas_30_dias, 0) || 0) +
      (parceiros?.reduce((sum, p) => sum + (p.propostas_carregadores_30_dias ?? 0), 0) || 0),
    totalContratos:
      (parceiros?.reduce((sum, p) => sum + p.contratos_fechados, 0) || 0) +
      (parceiros?.reduce((sum, p) => sum + (p.contratos_carregadores_fechados ?? 0), 0) || 0),
    contratosEsteMes:
      (parceiros?.reduce((sum, p) => sum + p.contratos_30_dias, 0) || 0) +
      (parceiros?.reduce((sum, p) => sum + (p.contratos_carregadores_30_dias ?? 0), 0) || 0),
    valorTotal: parceiros?.reduce((sum, p) => sum + (p.valor_contratos || 0) + (p.valor_contratos_carregadores ?? 0), 0) || 0
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Gerenciamento de Parceiros</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Acompanhe a performance dos seus parceiros e gerencie a rede de captação
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <Button 
            variant="outline" 
            size={isMobile ? "default" : "sm"}
            onClick={() => setShowFilters(!showFilters)}
            className={isMobile ? "w-full" : ""}
          >
            <FilterIcon className="h-4 w-4 sm:mr-2" />
            {!isMobile && (showFilters ? 'Ocultar Filtros' : 'Filtros')}
            {isMobile && (showFilters ? 'Ocultar' : 'Filtros')}
          </Button>
          <Button 
            variant="outline" 
            size={isMobile ? "default" : "sm"}
            onClick={refetch}
            disabled={loading}
            className={isMobile ? "w-full" : ""}
          >
            <RefreshCw className={`h-4 w-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            {!isMobile && 'Atualizar'}
          </Button>
          {podeCriarParceiro && (
            <Button 
              size={isMobile ? "default" : "sm"}
              onClick={() => setCadastroOpen(true)}
              className={isMobile ? "w-full" : ""}
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              {isMobile ? 'Novo Parceiro' : 'Novo Parceiro'}
            </Button>
          )}
        </div>
      </div>

      {/* Métricas Gerais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Total de Parceiros</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{metricsAgregadas.totalParceiros}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {metricsAgregadas.parceirosAtivos} ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Leads Capturados</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-blue-600">
              {metricsAgregadas.totalLeads}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              +{metricsAgregadas.leadsEsteMes} este mês
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Propostas Enviadas</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-green-600">
              {metricsAgregadas.totalPropostas}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              +{metricsAgregadas.propostasEsteMes} este mês
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Contratos Fechados</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-purple-600">
              {metricsAgregadas.totalContratos}
            </div>
            <p className="text-xs text-muted-foreground mt-1 break-words">
              {formatCurrency(metricsAgregadas.valorTotal)} em contratos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros (condicional) */}
      {showFilters && (
        <ParceiroFilters 
          filters={filters}
          onFiltersChange={setFilters}
        />
      )}

      {/* Busca */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={isMobile ? "Buscar parceiros..." : "Buscar parceiros por nome, email, cidade ou estado..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 text-sm sm:text-base"
          />
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600 text-sm">
              <strong>Erro:</strong> {error}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Informação de resultados */}
      {!loading && !error && (
        <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
          <p className="break-words">
            Exibindo {filteredParceiros.length} de {parceiros?.length || 0} parceiros
            {searchTerm && !isMobile && ` (filtrado por: "${searchTerm}")`}
            {searchTerm && isMobile && ` (filtrado)`}
          </p>
        </div>
      )}

      {/* Lista de Parceiros */}
      <ParceirosList 
        parceiros={filteredParceiros}
        loading={loading}
        onParceiroClick={setSelectedParceiro}
        onParceiroUpdated={refetch}
      />

      {/* Modal de Cadastro */}
      <CadastrarParceiro
        open={cadastroOpen}
        onClose={() => setCadastroOpen(false)}
        onSuccess={refetch}
      />

      {/* Modal de Detalhes */}
      <ParceiroDetails
        parceiroId={selectedParceiro}
        onClose={() => setSelectedParceiro(null)}
        onUpdate={refetch}
      />
    </div>
  );
}


